import React, { useState } from "react";
import {
  Button,
  TextInput,
  NumberInput,
  Switch,
} from "@tremor/react";
import * as profilesAPI from "../../services/profiles";
import RecentEventsDrawer from "./recent-events-drawer";

function hexToBase64(hex) {
  const clean = hex.replace(/\s+/g, "");
  if (clean.length === 0 || clean.length % 2 !== 0 || /[^0-9a-fA-F]/.test(clean)) {
    throw new Error("Invalid hex string");
  }
  let binary = "";
  for (let i = 0; i < clean.length; i += 2) {
    binary += String.fromCharCode(parseInt(clean.substr(i, 2), 16));
  }
  return btoa(binary);
}

const STATUS_STYLES = {
  ok:           { icon: "✅", cls: "text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950/40" },
  missing:      { icon: "❌", cls: "text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40" },
  undeclared:   { icon: "❔", cls: "text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-950/40" },
  out_of_range: { icon: "⚠️", cls: "text-yellow-800 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-950/40" },
  wrong_unit:   { icon: "⚠️", cls: "text-yellow-800 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-950/40" },
};

function ValidationPanel({ validation }) {
  if (!validation) return null;
  const fields = validation.fields || [];
  if (fields.length === 0) {
    return (
      <div className="mt-3 text-sm text-tremor-content dark:text-dark-tremor-content">
        No schema declared for this profile. Add one in the Schema tab to get
        per-field validation.
      </div>
    );
  }
  return (
    <div className="mt-3">
      <h4 className="mb-1">Contract check</h4>
      <ul className="space-y-1">
        {fields.map((f, i) => {
          const s = STATUS_STYLES[f.status] || { icon: "·", cls: "" };
          return (
            <li key={i} className={`px-2 py-1 text-sm ${s.cls}`}>
              <span className="mr-2">{s.icon}</span>
              <b>ch {f.channel}</b>
              {f.name ? ` — ${f.name}` : ""}
              {f.type ? ` (${f.type})` : ""}
              <span className="ml-2">{f.status}</span>
              {f.detail ? <span className="ml-2 opacity-75">— {f.detail}</span> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Test panel — sends a sample payload to the profile's decode endpoint and
 * renders the decoded output, errors, and per-field contract validation.
 *
 * Self-contained: owns all UI state, calls the API directly. Used both inside
 * the Custom-path TabGroup and inline for built-in decoder profiles.
 */
export default function TestPanel({ profile, onTestResult }) {
  const [payload, setPayload] = useState("");
  const [fPort, setFPort] = useState(1);
  const [isHexString, setIsHexString] = useState(false);
  const [output, setOutput] = useState("");
  const [error, setError] = useState(null);
  const [validation, setValidation] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const onTestClick = async () => {
    setError(null);
    setOutput("");
    setValidation(null);

    if (!payload) {
      console.log("Payload is required");
      return;
    }

    let apiPayload;
    try {
      apiPayload = isHexString ? hexToBase64(payload) : payload;
    } catch (err) {
      setError(`Invalid hex string: ${err.message}`);
      return;
    }

    const testedCode = profile.decoder || "";
    try {
      const resp = await profilesAPI.decode(profile._id, {
        payload: apiPayload,
        fPort: fPort,
      });
      const v = resp.validation || { fields: [] };
      setValidation(v);

      const hadErrors = resp.errors && resp.errors.length > 0;
      const hasFields = Array.isArray(v.fields) && v.fields.length > 0;
      const allFieldsOk = hasFields && v.fields.every((f) => f.status === "ok");

      let status;
      if (hadErrors) {
        status = "fail";
      } else if (!hasFields) {
        status = "no_schema";
      } else {
        status = allFieldsOk ? "pass" : "fail";
      }

      if (hadErrors) {
        setError(JSON.stringify(resp.errors, null, 5));
      } else {
        setOutput(JSON.stringify(resp.decoded, null, 2));
      }

      if (onTestResult) {
        onTestResult({ status, testedCode });
      }
    } catch (err) {
      console.log(err);
      if (onTestResult) {
        onTestResult({ status: "fail", testedCode });
      }
    }
  };

  const onPickEvent = ({ hex, fPort: pickedPort }) => {
    setPayload(hex);
    setIsHexString(true);
    if (pickedPort != null) setFPort(pickedPort);
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <h4 className="mb-1">Test Decoder</h4>
        <p className="mt-1 mb-2 text-tremor-default text-tremor-content dark:text-dark-tremor-content">
          Paste a raw payload, or pick one from a recent event to test the
          decoder function.
        </p>
        <label className="text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
          Raw Payload
        </label>
        <TextInput
          value={payload}
          className="mb-1"
          onValueChange={setPayload}
          placeholder="base64 e.g. AQIBGgAAAAAAAAA="
        />
        <div className="flex items-center space-x-3">
          <Switch
            id="switch"
            name="switch"
            checked={isHexString}
            onChange={setIsHexString}
          />
          <label
            htmlFor="switch"
            className="text-tremor-default text-tremor-content dark:text-dark-tremor-content"
          >
            Is Hex String{" "}
          </label>
        </div>
        <div className="mb-2">
          <label className="text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
            FPort
          </label>
          <NumberInput value={fPort} onValueChange={setFPort} />
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="xs" onClick={onTestClick}>
            Test
          </Button>
          <Button
            variant="light"
            size="xs"
            onClick={() => setDrawerOpen(true)}
          >
            Pick recent event
          </Button>
        </div>
      </div>
      <div>
        <h4 className="mb-1">Test Output</h4>
        {error && (
          <div className="bg-red-100 dark:bg-red-950/40 px-1">
            <pre className="text-red-500 dark:text-red-300">{error}</pre>
          </div>
        )}
        {output && (
          <div className="bg-green-100 dark:bg-green-950/40 p-2 border-1 border-gray-200 dark:border-gray-800 border-r">
            <pre>{output}</pre>
          </div>
        )}
        <ValidationPanel validation={validation} />
      </div>
      <RecentEventsDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onPick={onPickEvent}
      />
    </div>
  );
}
